import React, { useState, useEffect } from 'react';
import { Table } from '../components/ui/Table';
import { SearchFilter } from '../components/ui/SearchFilter';
import { Card } from '../components/ui/Card';
import { Download, ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';
import { esimsReport, EsimsReportFilters } from '../services/api';

export const EsimReport: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [expandedEsim, setExpandedEsim] = useState<string | null>(null);
  const [esims, setEsims] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [pagination, setPagination] = useState<{
    page: number;
    page_size: number;
    total: number;
  } | undefined>(undefined);

  // Load esims from API
  const loadEsims = async () => {
    setLoading(true);
    try {
      const apiFilters: EsimsReportFilters = {
        user_email: searchTerm || undefined,
        iccid: searchTerm || undefined,
        status: filters.status?.length ? filters.status : undefined,
      };
      
      // Use OR mode for general search (when searchTerm is provided and all fields have the same value)
      // Use AND mode for specific filters (when different filter values are provided)
      const hasGeneralSearch = searchTerm && !filters.status?.length;
      const searchMode = hasGeneralSearch ? 'OR' : 'AND';
      
      const result = await esimsReport(apiFilters, page, pageSize, searchMode);
      if (result && result.data) {
        setEsims(result.data);
        setPagination(result.pagination || undefined);
      }
    } catch (error) {
      console.error('Error loading esims:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEsims();
  }, [page, pageSize, searchTerm, filters]);

  // Handle page changes
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Handle page size changes
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1); // Reset to first page when changing page size
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSearchTerm('');
    setFilters({});
    setPage(1);
    setExpandedEsim(null); // Also reset expanded state
  };
  // Filter groups for the search filter component
  const filterGroups = [{
    id: 'status',
    label: 'Status',
    options: [
      { id: 'Requested', label: 'Requested' },
      { id: 'Pending', label: 'Pending' },
      { id: 'Active', label: 'Active' },
      { id: 'Suspended', label: 'Suspended' },
      { id: 'Failed', label: 'Failed' },
      { id: 'Canceled', label: 'Canceled' }
    ]
  }];
  // Table columns
  const columns = [{
    key: 'iccid',
    header: 'ICCID'
  }, {
    key: 'user_email',
    header: 'User'
  }, {
    key: 'status',
    header: 'Status',
    render: (value: string) => {
      const color = value === 'Activated' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
      return <span className={`px-2 py-1 rounded-full text-xs font-medium ${color}`}>
            {value}
          </span>;
    }
  }, {
    key: 'activated_date',
    header: 'Activation Date'
  }, {
    key: 'last_topup_date',
    header: 'Last Top-up Date'
  }, {
    key: 'actions',
    header: 'Actions',
    render: (_: any, row: any) => <button onClick={() => setExpandedEsim(expandedEsim === row.iccid ? null : row.iccid)} className="flex items-center text-indigo-600 hover:text-indigo-900">
          {expandedEsim === row.iccid ? <>
              <ChevronUp className="h-4 w-4 mr-1" />
              Hide Orders
            </> : <>
              <ChevronDown className="h-4 w-4 mr-1" />
              View Orders ({row.related_orders?.length || 0} orders, {row.related_topups?.length || 0} topups)
            </>}
        </button>
  }];
  return <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">E-SIM Report</h1>
          <p className="mt-1 text-sm text-gray-600">
            View and filter all e-sims
          </p>
        </div>
        <div className="flex space-x-3">
          <button 
            onClick={handleResetFilters}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset Filters
          </button>
          <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
            <Download className="h-4 w-4 mr-2" />
            Export
          </button>
        </div>
      </div>
      <Card>
        <SearchFilter placeholder="Search by ICCID or User..." filterGroups={filterGroups} onSearch={setSearchTerm} onFilter={setFilters} />
        <div className="overflow-hidden">
          <Table 
            columns={columns} 
            data={esims.map(esim => ({
              ...esim,
              activated_date: esim.activated_date || '-',
              last_topup_date: esim.last_topup_date || '-'
            }))} 
            isLoading={loading} 
            emptyMessage="No e-sims found matching your search criteria."
            pagination={pagination}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
        {/* Expanded order details */}
        {esims.map(esim => expandedEsim === esim.iccid && <div key={`${esim.iccid}-orders`} className="mt-2 bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  Order History for {esim.iccid}
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100">
                      <tr>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Order ID
                        </th>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Package
                        </th>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Price
                        </th>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {/* Related Orders */}
                      {(esim.related_orders || []).map((order: any, idx: number) => <tr key={order.name || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-blue-600 hover:text-blue-900">
                            <a href={`/orders?id=${order.name}`}>{order.name}</a>
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                            {order.package_name}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                            {order.creation}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                            {order.price ? `$${order.price.toFixed(2)}` : '-'}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm">
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Purchase
                            </span>
                          </td>
                        </tr>)}
                      {/* Related Topups */}
                      {(esim.related_topups || []).map((topup: any, idx: number) => <tr key={topup.name || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-blue-600 hover:text-blue-900">
                            <a href={`/orders?id=${topup.name}`}>{topup.name}</a>
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                            {topup.package_name}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                            {topup.creation}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                            {topup.price ? `$${topup.price.toFixed(2)}` : '-'}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm">
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Top-up
                            </span>
                          </td>
                        </tr>)}
                      {/* Show message if no orders or topups */}
                      {(!esim.related_orders || esim.related_orders.length === 0) && (!esim.related_topups || esim.related_topups.length === 0) && (
                        <tr>
                          <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                            No orders or topups found for this eSIM
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>)}
      </Card>
    </div>;
};